import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, ShoppingCart } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { supabase } from '../utils/supabaseClient';

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.

const Cart = () => {
  const { cart, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, clearCart, t } = useApp();
  const [showLogin, setShowLogin] = useState(false);
  const [customerData, setCustomerData] = useState({
    name: '',
    nit: '',
    company: '',
    email: '',
    phone: ''
  });

  const MAX_PRODUCTS = 20;
  const MAX_QUANTITY = 40;

  const handleCheckout = () => {
    if (cart.length === 0) return;

    if (cart.length > MAX_PRODUCTS) {
      alert("Máximo 20 productos diferentes por cotización, si desea licitar contacte a cualquiera de los asesores en la sección de contáctanos.");
      return;
    }

    setShowLogin(true);
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();

    // 1. Insertar cotización
    const { data, error } = await supabase
      .from('cotizaciones')
      .insert([{
        name: customerData.name,
        nit: customerData.nit,
        company: customerData.company,
        email: customerData.email,
        phone: customerData.phone,
        total_products: cart.length,
        total_quantity: cart.reduce((acc, item) => acc + item.quantity, 0)
      }])
      .select();

    console.log("DATA:", data);
    console.log("ERROR:", error);

    if (error) {
      console.error("Error cotización:", error);
      alert("Error cotización NO enviada, comuniquese con el área de soporte marcando al +57 3183473820.");
      return;
    }

    if (!data || data.length === 0) {
      alert("Error: no se creó la cotización");
      return;
    }

    const cotizacionId = data[0].id;

    // 2. Insertar productos del carrito
    const items = cart.map(item => ({
      cotizacion_id: cotizacionId,
      product_reference: item.reference,
      quantity: item.quantity
    }));

    const { error: itemsError } = await supabase
      .from('cotizacion_items')
      .insert(items);

    if (itemsError) {
      console.error("Error items:", itemsError);
    }

    alert(`¡Cotización enviada correctamente!\n\nCliente: ${customerData.name}`);

    setShowLogin(false);
    setIsCartOpen(false);
    clearCart();

    setCustomerData({
      name: '',
      nit: '',
      company: '',
      email: '',
      phone: ''
    });
  };

  return (
    <AnimatePresence mode="wait">
      {isCartOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, pointerEvents: "none" }}
          onClick={() => setIsCartOpen(false)}
        >
          <motion.div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <ShoppingCart className="w-6 h-6" />
                {t.cart.title}
              </h2>
              <button
                onClick={() => setIsCartOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[50vh] pr-2">
              {cart.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ShoppingCart className="w-10 h-10 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-lg">{t.cart.empty}</p>
                </div>
              ) : (
                <div className="p-6 space-y-4">
                  {cart.map((item) => (
                    <motion.div
                      key={item.id}
                      className="flex items-center gap-4 p-4 border rounded-xl"
                      layout
                    >
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold">{item.name}</h3>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-semibold">{item.quantity}</span>
                        <button
                          onClick={() => {
                            if (item.quantity >= MAX_QUANTITY) {
                              alert("Máximo 40 unidades por producto, si desea licitar comuniquese con cualquiera de los asesores comerciales en la sección de contáctanos.");
                              return;
                            }
                            updateQuantity(item.id, item.quantity + 1);
                          }}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="border-t p-6">
                                
                <motion.button
                  onClick={handleCheckout}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-800 text-white py-3 rounded-xl font-semibold text-lg hover:shadow-lg"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {t.cart.checkout}
                </motion.button>
              </div>
            )}

            <AnimatePresence>
              {showLogin && (
                <motion.div
                  className="fixed inset-0 flex items-center justify-center bg-black/40 z-[60] p-4 overflow-y-auto"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >

                  <motion.div
                    className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                  >
                    <h3 className="text-xl font-bold mb-6 text-center">{t.cart.login}</h3>

                    <form onSubmit={handleSubmitOrder} className="space-y-4">
                      <input
                        type="text"
                        placeholder="Nombre completo"
                        value={customerData.name}
                        onChange={(e) => setCustomerData({...customerData, name: e.target.value})}
                        required
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="NIT - CC"
                        value={customerData.nit}
                        onChange={(e) => setCustomerData({ ...customerData, nit: e.target.value })}
                        required
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="Empresa"
                        value={customerData.company}
                        onChange={(e) => setCustomerData({ ...customerData, company: e.target.value })}
                        required
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      
                      <input
                        type="email"
                        placeholder="Correo electrónico"
                        value={customerData.email}
                        onChange={(e) => setCustomerData({...customerData, email: e.target.value})}
                        required
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      
                      <input
                        type="tel"
                        placeholder="Número de teléfono"
                        value={customerData.phone}
                        onChange={(e) => setCustomerData({...customerData, phone: e.target.value})}
                        required
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      
                      <div className="flex gap-3 pt-4">
                        <button
                          type="button"
                          onClick={() => setShowLogin(false)}
                          className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold"
                          >
                            Cancelar
                          </button>
                          
                          <button
                            type="submit"
                            className="flex-1 bg-gradient-to-r from-blue-500 to-blue-800 text-white py-3 rounded-lg font-semibold"
                          >
                            Confirmar Cotización
                        </button>
                      </div>
                    </form>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Cart;

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.